/**
 * Historique des produits consultés.
 *
 * Fichier custom (hors référentiel FullStack) : il survit aux mises à jour du thème.
 *
 * - `RecentlyViewedManager` : stockage des IDs produits dans le localStorage,
 *   du plus récent au plus ancien (même logique que `WishlistManager`).
 * - `<recently-viewed-tab>` : la pastille d'onglet, qui se retire d'elle-même
 *   quand l'historique est vide.
 * - `<recently-viewed-products>` : le panneau, qui récupère les cartes produit
 *   auprès de la section de rendu `recently-viewed-products` puis les injecte
 *   dans la coquille (grille ou slider) préparée par la section hôte.
 */

const STORAGE_KEY = 'recently-viewed';
const MAX_STORED_PRODUCTS = 20;

/**
 * Cache des cartes déjà récupérées, au niveau du module.
 *
 * `tabs.js` reconstruit le contenu du panneau depuis le <template> à chaque
 * bascule : l'élément <recently-viewed-products> est donc recréé à neuf, et un
 * cache porté par l'instance ne servirait à rien. Ici, un retour sur l'onglet
 * réaffiche les cartes de façon synchrone, sans appel réseau ni panneau vide.
 */
let cachedItems = null;
let pendingFetch = null;

function parseItems(html) {
  return [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-recently-viewed-item]')];
}

function readCache(ids) {
  return cachedItems && cachedItems.key === ids.join(',') ? parseItems(cachedItems.html) : null;
}

/**
 * Récupère les cartes auprès de la section de rendu, via la route recherche.
 * Un seul appel réseau au plus : le résultat est mis en cache, et un appel
 * concurrent réutilise la promesse en cours.
 */
function fetchItems(url, sectionId, ids) {
  const key = ids.join(',');

  if (pendingFetch && pendingFetch.key === key) return pendingFetch.promise;

  const query = ids.map((id) => `id:${id}`).join(' OR ');
  const endpoint = `${url}?q=${encodeURIComponent(query)}&type=product&section_id=${sectionId}`;

  const promise = fetch(endpoint)
    .then((response) => {
      if (!response.ok) throw new Error(`Recently viewed request failed with status ${response.status}`);
      return response.text();
    })
    .then((html) => {
      // La recherche Shopify ne garantit pas l'ordre des résultats : on rétablit
      // l'ordre de consultation, du plus récent au plus ancien.
      const items = parseItems(html).sort(
        (a, b) => ids.indexOf(Number(a.dataset.productId)) - ids.indexOf(Number(b.dataset.productId)),
      );

      if (items.length > 0) cachedItems = { key, html: items.map((item) => item.outerHTML).join('') };

      return items;
    })
    .finally(() => {
      if (pendingFetch && pendingFetch.key === key) pendingFetch = null;
    });

  pendingFetch = { key, promise };

  return promise;
}

class RecentlyViewedManager {
  static getIds() {
    let raw = null;

    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return [];
    }

    if (!raw) return [];

    try {
      const ids = JSON.parse(raw);
      if (!Array.isArray(ids)) return [];

      return ids.map(Number).filter((id) => Number.isFinite(id) && id > 0);
    } catch (e) {
      console.error('Error parsing recently viewed storage:', e);
      return [];
    }
  }

  static setIds(ids) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      console.error('Error writing recently viewed storage:', e);
    }
  }

  static track(productId) {
    const id = Number(productId);
    if (!Number.isFinite(id) || id <= 0) return;

    const ids = this.getIds().filter((storedId) => storedId !== id);
    ids.unshift(id);

    this.setIds(ids.slice(0, MAX_STORED_PRODUCTS));
  }

  static getIdsExcluding(excludedId, limit) {
    const excluded = Number(excludedId);

    return this.getIds()
      .filter((id) => id !== excluded)
      .slice(0, limit);
  }
}

// Enregistre le produit courant : l'ID est posé par `snippets/scripts.liquid`
// sur toutes les pages produit, que la section soit présente ou non.
if (window.recentlyViewedProductId) {
  RecentlyViewedManager.track(window.recentlyViewedProductId);
}

/**
 * ID du produit de la page courante, à exclure de la liste.
 *
 * Il est lu sur la section hôte : au moment du rendu asynchrone des
 * recommandations, l'objet `product` de Liquid n'est plus celui de la page.
 */
function getCurrentProductId(element) {
  return element.closest('product-recommendations')?.dataset.productId || window.recentlyViewedProductId;
}

/**
 * Retire un onglet du conteneur d'onglets, et masque toute la section
 * s'il ne reste plus rien à afficher.
 */
function removeTab(tab) {
  const tabsContainer = tab?.closest('tabs-container');

  tab?.remove();

  if (!tabsContainer) return;

  const remainingTabs = tabsContainer.querySelectorAll('[data-ref="tab"]');
  const panel = tabsContainer.querySelector('[data-ref="tabs-panel"]');

  if (remainingTabs.length === 0) {
    const section = tabsContainer.closest('product-recommendations');
    if (section) section.style.display = 'none';
    return;
  }

  tabsContainer.classList.toggle('tabs--single', remainingTabs.length === 1);

  // Si l'onglet retiré était celui affiché, on bascule sur le premier restant.
  if (panel && !tabsContainer.querySelector('[data-ref="tab"].active')) {
    remainingTabs[0].click();
  }
}

/**
 * Empêche le panneau de s'effondrer pendant une bascule d'onglet.
 *
 * `tabs.js` vide le panneau (`innerHTML = ''`) avant d'y injecter le contenu
 * suivant : la hauteur retombe à 0 et la page saute. On fige donc la hauteur
 * courante en phase de capture — donc avant les écouteurs de `tabs.js` — et on
 * la relâche dès que les nouvelles cartes sont posées.
 */
function holdPanelHeight(tabsContainer) {
  const panel = tabsContainer.querySelector('[data-ref="tabs-panel"]');

  if (!panel || tabsContainer.dataset.panelHeightHeld) return;
  tabsContainer.dataset.panelHeightHeld = 'true';

  let currentRelease = null;

  tabsContainer.addEventListener(
    'click',
    (event) => {
      if (!event.target.closest('[data-ref="tab"]')) return;

      currentRelease?.();

      const height = panel.getBoundingClientRect().height;
      if (height <= 0) return;

      panel.style.minHeight = `${height}px`;

      // Relâchée dès que les cartes du nouvel onglet sont posées, ou après 1 s
      // si le chargement échoue — pour ne jamais laisser une hauteur figée.
      // MutationObserver plutôt que requestAnimationFrame : les rAF ne se
      // déclenchent pas quand l'onglet du navigateur passe en arrière-plan.
      const observer = new MutationObserver(() => {
        if (panel.querySelector('product-card')) currentRelease?.();
      });

      const timer = setTimeout(() => currentRelease?.(), 1000);

      currentRelease = () => {
        currentRelease = null;
        observer.disconnect();
        clearTimeout(timer);
        panel.style.minHeight = '';
      };

      observer.observe(panel, { childList: true, subtree: true });
    },
    true,
  );
}

class RecentlyViewedTab extends HTMLElement {
  connectedCallback() {
    const limit = parseInt(this.dataset.limit, 10) || 4;
    const ids = RecentlyViewedManager.getIdsExcluding(getCurrentProductId(this), limit);

    if (ids.length === 0) {
      removeTab(this);
      return;
    }

    // Cet élément est le seul que l'on contrôle qui survive aux bascules
    // d'onglet : il vit dans la navigation, pas dans le panneau reconstruit.
    const tabsContainer = this.closest('tabs-container');
    if (tabsContainer) holdPanelHeight(tabsContainer);

    this.#warmCache(ids);
  }

  /**
   * Charge les cartes dès l'apparition de la section, sans attendre le clic :
   * l'onglet s'affiche alors instantanément, y compris la première fois.
   */
  #warmCache(ids) {
    const panel = this.querySelector('template')?.content.querySelector('recently-viewed-products');

    if (!panel || readCache(ids)) return;

    fetchItems(panel.dataset.url, panel.dataset.sectionId, ids).catch(() => {
      // Silencieux : l'erreur sera rejouée et signalée au clic sur l'onglet.
    });
  }
}

class RecentlyViewedProducts extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    if (this.#loaded) return;
    this.#loaded = true;

    this.#load();
  }

  async #load() {
    const limit = parseInt(this.dataset.limit, 10) || 4;
    const ids = RecentlyViewedManager.getIdsExcluding(getCurrentProductId(this), limit);

    if (ids.length === 0) {
      this.#giveUp();
      return;
    }

    // Lu avant tout `await` : sur un retour d'onglet le remontage reste
    // synchrone, donc le panneau n'est jamais vide.
    const cached = readCache(ids);

    if (cached) {
      this.#mount(cached);
      return;
    }

    try {
      const items = await fetchItems(this.dataset.url, this.dataset.sectionId, ids);

      if (items.length === 0) {
        this.#giveUp();
        return;
      }

      this.#mount(items);
    } catch (e) {
      console.error(e);
      this.#giveUp();
    }
  }

  #mount(items) {
    const shell = this.querySelector('template[data-ref="recently-viewed-shell"]');
    if (!shell) return;

    const content = shell.content.cloneNode(true);

    // La coquille contient une mise en page par appareil quand desktop et mobile
    // ne sont pas réglés pareil : on remplit chacune.
    const targets = [
      ...[...content.querySelectorAll('.splide__list')].map((container) => ({ container, wrapInSlide: true })),
      ...[...content.querySelectorAll('[data-ref="recently-viewed-grid"]')].map((container) => ({
        container,
        wrapInSlide: false,
      })),
    ];

    targets.forEach(({ container, wrapInSlide }) => {
      items.forEach((item) => {
        const card = item.firstElementChild?.cloneNode(true);
        if (!card) return;

        container.appendChild(wrapInSlide ? this.#wrapInSlide(card) : card);
      });
    });

    // Les sliders se montent à la connexion : on n'insère qu'une fois rempli.
    shell.replaceWith(content);
  }

  #wrapInSlide(card) {
    const slide = document.createElement('li');
    slide.className = 'splide__slide';

    const inner = document.createElement('div');
    inner.className = 'slide';
    inner.setAttribute('style', '--gap: 0px; --margin-top: 0px; --margin-bottom: 0px;');
    inner.appendChild(card);

    slide.appendChild(inner);

    return slide;
  }

  #giveUp() {
    const tabsContainer = this.closest('tabs-container');
    removeTab(tabsContainer?.querySelector('recently-viewed-tab'));
  }
}

if (!customElements.get('recently-viewed-tab')) {
  customElements.define('recently-viewed-tab', RecentlyViewedTab);
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
